import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import useCreateStaffAvailability from 'hooks/staffAvailability/useCreateStaffAvailability';
import { DAY_OF_WEEK_TITLE_MAP, DayOfWeekEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';

interface CreateWeekDayStaffAvailabilityProps {
  opened: boolean;
  dayOfWeek: DayOfWeekEnum;
  handleClose: () => void;
}

interface FormValues {
  startTime: string;
  endTime: string;
}

const CreateStaffAvailabilityModal = ({
  opened,
  dayOfWeek,
  handleClose,
}: CreateWeekDayStaffAvailabilityProps): JSX.Element => {
  const createMutation = useCreateStaffAvailability();
  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      startTime: '00:00:00',
      endTime: '23:59:00',
    },

    validate: values => ({
      startTime: values.startTime ? null : 'Start time is required',
      endTime: values.endTime ? null : 'End time is required',
    }),
  });

  const handleCloseAndReset = (): void => {
    form.reset();
    handleClose();
  };

  const handleSave = (formValues: FormValues): void => {
    createMutation.mutate(
      { ...formValues, dayOfWeek: dayOfWeek },
      {
        onError: error => {
          errorNotification('Availability creation failed', error.response?.data.message);
        },
        onSuccess: response => {
          successNotification(
            'Availability creation success',
            `On ${DAY_OF_WEEK_TITLE_MAP[response.data.dayOfWeek]} from ${response.data.startTime} to ${
              response.data.endTime
            } successfully created.`,
          );
          handleCloseAndReset();
        },
      },
    );
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={3}>Add Weekly Schedule Availability</Title>}
      size='auto'
      overlayProps={{
        opacity: 0.55,
      }}
    >
      <form onSubmit={form.onSubmit(handleSave)}>
        <Stack p='sm'>
          <Text size='lg'>
            When are you available on{' '}
            <Text span weight={600}>
              {DAY_OF_WEEK_TITLE_MAP[dayOfWeek]}
            </Text>{' '}
            ?
          </Text>

          <TimeInput label='From' withAsterisk {...form.getInputProps('startTime')} />
          <TimeInput label='To' withAsterisk {...form.getInputProps('endTime')} />

          <Group position='right' pt='sm'>
            <Button onClick={handleCloseAndReset} disabled={createMutation.isLoading} variant='subtle'>
              Cancel
            </Button>
            <Button type='submit' loading={createMutation.isLoading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CreateStaffAvailabilityModal;
